<template>
    <span :id="`select-${id}`"
        :class="['select', { 'select--disabled': isDisabled }]"
    >
        <div v-if="filter"
            :id="`select-filter-${id}`"
            class="select-filter"
        >
            <input :id="`select-filter__input-${id}`"
                v-model="search"
                type="text"
                :disabled="isDisabled"
                :placeholder="$t('global.search.search')"
                :class="['select-filter__input', { 'select-filter__input--disabled': isDisabled }]"
            >
        </div>

        <ul v-if="options.length"
            :id="`select-list-${id}`"
            :style="{ maxHeight: `${maxListHeight}rem` }"
            :class="[
                'select-list',
                {
                    'select-list--multi': multi,
                    'select-list--disabled': isDisabled
                }
            ]"
        >
            <li v-for="(item, index) in filteredOptions"
                :id="`select-list__option-${id}-${index}`"
                :key="index"
                :class="[
                    'select-list__option',
                    'option',
                    {
                        'select-list__option--selected': isOptionSelected(item),
                        'select-list__option--toggled': isOptionSelected(item) && toggle,
                        'select-list__option--disabled': isDisabled
                    }
                ]"
                @click="(toggle && !isOptionSelected(item)) || !toggle ? updateSelected(item) : null"
            >
                <span :id="`option__text-${id}-${index}`"
                    class="option__text"
                >
                    {{ item.text }}
                </span>

                <i :id="`option__icon-${id}-${index}`"
                    :class="['option__icon', getIcon(item)]"
                />
            </li>
        </ul>

        <div v-if="multi && customOptions.length"
            :id="`select-custom-${id}`"
            class="select-custom"
        >
            <label class="select-custom__header">{{ $t('global.search.custom') }}</label>

            <input v-for="(item, index) in customOptions"
                :id="`select-custom__input-${id}-${index}`"
                :key="index"
                v-model="item.value"
                v-debounce:300ms="($event) => updateSelected(item, true)"
                :type="item.type || 'text'"
                :placeholder="item.text"
                class="select-custom__input"
            >
        </div>
    </span>
</template>

<script>

export default {
    name: 'custom-select',
    props: {
        // Id for automation
        id: {
            type: String,
            default: '',
        },
        // Array of objects with text and value properties
        items: {
            type: Array,
            default: () => [],
            required: true,
        },
        // Array of objects with text and value properties
        selected: {
            type: Array,
            default: () => [],
            required: false,
        },
        // Boolean for enabling and multi select
        multi: {
            type: Boolean,
            default: false,
        },
        // Boolean flag to enable the select to behave as a toggle, meaning it requires an active value at all times
        toggle: {
            type: Boolean,
            default: false,
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        // Boolean for enabling and disabling client side filtering
        filter: {
            type: Boolean,
            default: false,
        },
        // Array of objects with text, value and type properties
        customItems: {
            type: Array,
            default: () => [],
            required: false,
        },
        maxListHeight: {
            type: Number,
            default: 15,
            required: false,
        },
    },
    data() {
        return {
            options: [],
            selectedOptions: [],
            customOptions: [],
            search: '',
        };
    },
    computed: {
        filteredOptions() {
            return this.options.filter(({
                text,
            }) => text.toLowerCase().includes(this.search.toLowerCase()));
        },
    },
    watch: {
        items: {
            immediate: true,
            handler(value) {
                this.options = value;
            },
        },

        selected: {
            immediate: true,
            handler(value) {
                this.selectedOptions = value;
            },
        },

        customItems: {
            immediate: true,
            handler(value) {
                this.customOptions = value;
            },
        },
    },
    mounted() {
        if (this.selectedOptions.length) {
            this.$emit('selection', this.selectedOptions);
        }
    },
    methods: {
        updateSelected(option, custom = false) {
            const foundIndex = this.selectedOptions.findIndex(({
                text,
            }) => text === option.text);

            if (custom) {
                this.selectedOptions = this.handleCustomOptions(foundIndex, option, this.selectedOptions);
            } else {
                this.selectedOptions = this.handlePredefinedOptions(foundIndex, option, this.selectedOptions);
            }

            this.$emit('selection', this.selectedOptions);
        },

        /*
            Function for handling updating the selected options for custom options
            If the option is new we add it
            If the option exists we override it
            If the option exists and there is no value we omit it
        */
        handleCustomOptions(foundIndex, option, selectedOptions) {
            if (foundIndex >= 0) {
                return option.value
                    ? selectedOptions.map((selectedOption, index) => {
                        if (index === foundIndex) {
                            return option;
                        }

                        return selectedOption;
                    })
                    : selectedOptions.filter((o, index) => index !== foundIndex);
            }

            return [
                ...selectedOptions,
                option,
            ];
        },

        /*
            Function for handling updating the selected options for predefined options
            If the option is new we add it
            If option exists we remove it
        */
        handlePredefinedOptions(foundIndex, option, selectedOptions) {
            if (foundIndex >= 0) {
                return this.multi ? selectedOptions.filter((o, index) => index !== foundIndex) : [];
            }

            return this.multi ? [
                ...selectedOptions,
                option,
            ] : [option];
        },

        isOptionSelected(option) {
            return this.selectedOptions.some((selectedOption) => JSON.stringify(selectedOption) === JSON.stringify(option));
        },

        getIcon(option) {
            if (this.isOptionSelected(option)) {
                return 'icon-cancel';
            }

            return 'icon-ticknocircle';
        },
    },
};
</script>

<style lang="scss" scoped>
.select {
    max-width: 16rem;
    position: relative;
    display: block;

    &--disabled {
        pointer-events: none;

        &:hover {
            cursor: not-allowed;
        }
    }

    &-list {
        list-style-type: none;
        padding: 0;
        font-size: 1.2rem;
        color: $dusty-gray-color;
        max-height: 15rem;
        overflow-x: hidden;
        overflow-y: auto;
        @include scrollbar(transparent, $dusty-gray-color, 4);

        &--multi {
            color: $cyprus-color;
        }

        &--disabled {
            overflow-y: hidden;
        }

        &__option {
            cursor: pointer;
            padding-bottom: 1.4rem;

            &:hover {
                color: $lima-color;

                .option {
                    &__icon {
                        visibility: visible;
                    }
                }
            }

            .option {
                &__text,
                &__icon {
                    font-weight: inherit;
                }

                &__text {
                    padding-right: 1rem;
                }

                &__icon {
                    font-size: 1rem;
                    visibility: hidden;
                }
            }

            &--selected {
                font-weight: 600;
                color: $cyprus-color;

                .option {
                    &__icon {
                        visibility: visible;
                    }
                }
            }

            &--disabled {
                opacity: 0.5;
            }

            &--toggled {
                font-weight: 600;
                color: $cyprus-color;
                cursor: default;

                &:hover {
                    color: $cyprus-color;

                    .option {
                        &__icon {
                            visibility: hidden;
                        }
                    }
                }

                .option {
                    &__icon {
                        visibility: hidden;
                    }
                }
            }
        }
    }

    &-custom,
    &-filter {
        font-size: 1.2rem;
        display: flex;
        flex-direction: column;
        color: $cyprus-color;

        &__input {
            border: 0.08rem solid $porcelain-dark-color;
            font-family: "Nunito";
            border-radius: 0.4rem;
            width: 7.5rem;
            height: 3.5rem;
            margin-top: 1rem;
            padding-left: 0.5rem;

            &--disabled {
                opacity: 0.5;
            }
        }
    }

    &-filter {
        margin-bottom: 2rem;
    }

    &-custom {
        &__header {
            color: $dusty-gray-color;
        }

        &__input {
            margin-top: 1rem;

            &::placeholder {
                opacity: 0.5;
            }
        }
    }
}
</style>